import React, { useEffect, useMemo, useState } from "react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { UserAuthData } from "Types/UserDataTypes";
import {
  Button,
  Center,
  Group,
  Loader,
  Text,
  TextInput,
  Textarea,
  Title,
  ColorInput,
  useMantineColorScheme,
  Container,
  Badge,
  Paper,
  Divider,
  Modal,
  Checkbox,
  Select,
  Table,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate, useParams } from "react-router-dom";
import {
  IconArrowLeft,
  IconTrash,
  IconPlus,
  IconLock,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useAdminRoleByIdQuery,
  useAdminAuthActionsQuery,
  useAdminActionCategoriesQuery,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useAssignActionToRoleMutation,
  useUnassignActionFromRoleMutation,
  useSyncDiscoveredActionsMutation,
} from "generated/graphql";

// Super Admin role has access to everything - this is the canonical name from the backend
const SUPER_ADMIN_ROLE = "Super Admin";

/**
 * RoleEdit
 *
 * Admin page for viewing and editing a role.
 * Allows editing role details and managing action assignments.
 * System roles can be edited but not deleted (unless user is Super Admin).
 */
export default function RoleEdit() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  // Check if current user is Super Admin
  const authData = useAuthUser<UserAuthData>();
  const isSuperAdmin = authData?.roles?.includes(SUPER_ADMIN_ROLE) ?? false;

  // Queries
  const {
    data: roleData,
    loading: roleLoading,
    error: roleError,
    refetch: refetchRole,
  } = useAdminRoleByIdQuery({
    variables: { id: roleId! },
    skip: !roleId,
    fetchPolicy: "cache-and-network",
  });

  const { data: categoriesData } = useAdminActionCategoriesQuery();

  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const { data: actionsData, refetch: refetchActions } = useAdminAuthActionsQuery({
    variables: { category: categoryFilter || undefined },
    fetchPolicy: "cache-and-network",
  });

  // Mutations
  const [updateRole, { loading: saving }] = useUpdateRoleMutation();
  const [deleteRole, { loading: deleting }] = useDeleteRoleMutation();
  const [assignAction, { loading: assigning }] = useAssignActionToRoleMutation();
  const [unassignAction, { loading: unassigning }] = useUnassignActionFromRoleMutation();
  const [syncActions, { loading: syncing }] = useSyncDiscoveredActionsMutation();

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("");
  const [iconUrl, setIconUrl] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // Modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addActionModalOpen, setAddActionModalOpen] = useState(false);

  const role = roleData?.role;
  const assignedActionKeys = useMemo(
    () => new Set((role?.actions ?? []).map((a) => a.action)),
    [role]
  );

  // All available actions
  const allActions = useMemo(() => actionsData?.authActions ?? [], [actionsData]);

  // Actions not yet assigned to this role
  const availableActions = useMemo(
    () => allActions.filter((a) => !assignedActionKeys.has(a.key)),
    [allActions, assignedActionKeys]
  );

  // Category options for filter
  const categoryOptions = useMemo(
    () =>
      [{ value: "", label: "All Categories" }].concat(
        (categoriesData?.actionCategories ?? []).map((c) => ({
          value: c,
          label: c,
        }))
      ),
    [categoriesData]
  );

  // Populate form when data loads
  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description ?? "");
      setColor(role.color ?? "");
      setIconUrl(role.iconUrl ?? "");
      setHasChanges(false);
    }
  }, [role]);

  // Track changes
  useEffect(() => {
    if (role) {
      const changed =
        name !== role.name ||
        description !== (role.description ?? "") ||
        color !== (role.color ?? "") ||
        iconUrl !== (role.iconUrl ?? "");
      setHasChanges(changed);
    }
  }, [name, description, color, iconUrl, role]);

  async function handleSave() {
    if (!roleId) return;

    try {
      const result = await updateRole({
        variables: {
          id: roleId,
          input: {
            name: name.trim() || undefined,
            description: description.trim() || undefined,
            color: color || undefined,
            iconUrl: iconUrl.trim() || undefined,
          },
        },
      });

      if (result.data?.updateRole?.success) {
        notifications.show({
          title: "Success",
          message: "Role updated successfully",
          color: "green",
        });
        refetchRole();
      } else {
        notifications.show({
          title: "Error",
          message: result.data?.updateRole?.errorMessage || "Failed to update role",
          color: "red",
        });
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to update role",
        color: "red",
      });
    }
  }

  async function handleDelete() {
    if (!roleId) return;

    try {
      const result = await deleteRole({
        variables: { id: roleId },
      });

      if (result.data?.deleteRole?.success) {
        notifications.show({
          title: "Deleted",
          message: "Role has been deleted",
          color: "green",
        });
        navigate("/admin-hub/roles");
      } else {
        notifications.show({
          title: "Error",
          message: result.data?.deleteRole?.errorMessage || "Failed to delete role",
          color: "red",
        });
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to delete role",
        color: "red",
      });
    }
    setDeleteModalOpen(false);
  }

  async function handleAssignAction(actionKey: string) {
    if (!roleId) return;

    try {
      const result = await assignAction({
        variables: { roleId, actionKey },
      });

      if (result.data?.assignActionToRole?.success) {
        notifications.show({
          title: "Action Assigned",
          message: `Added "${actionKey}" to role`,
          color: "green",
        });
        refetchRole();
      } else {
        notifications.show({
          title: "Error",
          message:
            result.data?.assignActionToRole?.errorMessage || "Failed to assign action",
          color: "red",
        });
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to assign action",
        color: "red",
      });
    }
  }

  async function handleUnassignAction(actionKey: string) {
    if (!roleId) return;

    try {
      const result = await unassignAction({
        variables: { roleId, actionKey },
      });

      if (result.data?.unassignActionFromRole?.success) {
        notifications.show({
          title: "Action Removed",
          message: `Removed "${actionKey}" from role`,
          color: "green",
        });
        refetchRole();
      } else {
        notifications.show({
          title: "Error",
          message:
            result.data?.unassignActionFromRole?.errorMessage ||
            "Failed to remove action",
          color: "red",
        });
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to remove action",
        color: "red",
      });
    }
  }

  async function handleSyncActions() {
    try {
      const result = await syncActions();
      if (result.data?.syncDiscoveredActions) {
        const { created, totalDiscovered } = result.data.syncDiscoveredActions;
        notifications.show({
          title: "Actions Synced",
          message: `${created.length} new actions added. ${totalDiscovered} total discovered.`,
          color: "green",
        });
        refetchActions();
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to sync actions",
        color: "red",
      });
    }
  }

  if (roleLoading && !roleData) {
    return (
      <Center style={{ height: "calc(100dvh - 48px)" }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (roleError || !role) {
    return (
      <Container size="xl" px="lg" py="lg">
        <Group justify="flex-start" mb="sm">
          <Button
            variant="filled"
            color="blue"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/admin-hub/roles")}
          >
            Back to Roles
          </Button>
        </Group>
        <Text c="red">Failed to load role data.</Text>
      </Container>
    );
  }

  const isProcessing = saving || deleting || assigning || unassigning;

  return (
    <Container size="xl" px="lg" py="lg">
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin-hub/roles")}
          size="sm"
        >
          Back to Roles
        </Button>
      </Group>

      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Group gap="sm">
              <Title c={isDark ? "white" : "black"}>{role.name}</Title>
              {role.isSystem && (
                <Badge leftSection={<IconLock size={12} />} color="gray">
                  System Role
                </Badge>
              )}
            </Group>
            <Text c={isDark ? "gray.5" : "gray.6"} size="sm" mt="xs">
              ID: {roleId}
            </Text>
          </div>
          {role.color && (
            <Badge
              style={{ backgroundColor: role.color, color: "#fff" }}
              size="lg"
            >
              {role.name}
            </Badge>
          )}
        </div>

        <Divider />

        {/* Role Details Form */}
        <Paper withBorder p="md" radius="md">
          <Title order={4} mb="md">
            Role Details
          </Title>

          <div className="space-y-4">
            <TextInput
              label="Role Name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              withAsterisk
            />

            <Textarea
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              minRows={2}
            />

            <ColorInput
              label="Color"
              value={color}
              onChange={setColor}
              format="hex"
              swatches={[
                "#DC2626",
                "#F59E0B",
                "#10B981",
                "#3B82F6",
                "#8B5CF6",
                "#EC4899",
                "#6B7280",
              ]}
            />

            <TextInput
              label="Icon URL"
              value={iconUrl}
              onChange={(e) => setIconUrl(e.currentTarget.value)}
            />

            <Group mt="md">
              <Button
                className="bg-blue-500 hover:bg-blue-600"
                onClick={handleSave}
                disabled={!hasChanges || saving}
                loading={saving}
              >
                Save Changes
              </Button>
              {(!role.isSystem || isSuperAdmin) && (
                <Button
                  color="red"
                  variant="light"
                  onClick={() => setDeleteModalOpen(true)}
                  disabled={isProcessing}
                >
                  {role.isSystem ? "Delete System Role" : "Delete Role"}
                </Button>
              )}
            </Group>
          </div>
        </Paper>

        <Divider />

        {/* Actions Section */}
        <Paper withBorder p="md" radius="md">
          <Group justify="space-between" mb="md">
            <Title order={4}>Assigned Actions ({role.actions.length})</Title>
            <Group>
              <Tooltip label="Sync actions from code to database">
                <Button
                  variant="light"
                  color="gray"
                  size="xs"
                  leftSection={<IconRefresh size={14} />}
                  onClick={handleSyncActions}
                  loading={syncing}
                >
                  Sync Actions
                </Button>
              </Tooltip>
              <Button
                size="xs"
                color="blue"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                leftSection={<IconPlus size={14} />}
                onClick={() => setAddActionModalOpen(true)}
                disabled={isProcessing}
              >
                Add Action
              </Button>
            </Group>
          </Group>

          {role.actions.length === 0 ? (
            <Text c="dimmed" size="sm">
              No actions assigned to this role yet. Add some to grant permissions.
            </Text>
          ) : (
            <Table striped withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Action Key</Table.Th>
                  <Table.Th w={80}>Remove</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {role.actions.map((a) => (
                  <Table.Tr key={a.id}>
                    <Table.Td>
                      <Text size="sm" ff="monospace">
                        {a.action}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => handleUnassignAction(a.action)}
                        disabled={unassigning}
                        title="Remove action"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={role.isSystem ? "Delete System Role?" : "Delete Role?"}
        centered
      >
        {role.isSystem && (
          <Text size="sm" mb="md" c="red" fw={600}>
            Warning: You are about to delete a system role. This is a critical action
            that may affect core system functionality.
          </Text>
        )}
        <Text size="sm" mb="lg">
          Are you sure you want to delete the role "{role.name}"? This action cannot
          be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="light" color="gray" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete} loading={deleting}>
            Delete
          </Button>
        </Group>
      </Modal>

      {/* Add Action Modal */}
      <Modal
        opened={addActionModalOpen}
        onClose={() => setAddActionModalOpen(false)}
        title="Add Action to Role"
        size="lg"
        centered
      >
        <div className="space-y-4">
          <Select
            label="Filter by Category"
            data={categoryOptions}
            value={categoryFilter || ""}
            onChange={(v) => setCategoryFilter(v || null)}
            clearable
          />

          {availableActions.length === 0 ? (
            <Text c="dimmed" size="sm">
              No available actions to add
              {categoryFilter ? " in this category" : ""}.
            </Text>
          ) : (
            <div
              style={{ maxHeight: "400px", overflow: "auto" }}
              className="border rounded p-2"
            >
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Action</Table.Th>
                    <Table.Th>Category</Table.Th>
                    <Table.Th w={80}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {availableActions.map((action) => (
                    <Table.Tr key={action.key}>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {action.key}
                        </Text>
                        {action.description && (
                          <Text size="xs" c="dimmed">
                            {action.description}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {action.category || "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="light"
                          color="blue"
                          onClick={() => handleAssignAction(action.key)}
                          disabled={assigning}
                        >
                          Add
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}
        </div>
      </Modal>
    </Container>
  );
}
